import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";
import InformationCircleIcon from "../../components/Icons/InformationCircleIcon";
import AutocompleteSearch from "../../components/AutocompleteSearch";
import { UsersGroup as IconUsers, Star as StarOutlineIcon, BadgeCheck as IconBadgeCheck } from "flowbite-react-icons/outline";
import { Star as StarFilledIcon } from "flowbite-react-icons/solid";
import { Check as IconCheck } from "flowbite-react-icons/outline";
import Checkbox from "../../components/Checkbox";
import CustomButton from "../../components/CustomButton";
import Tooltip from "../../components/Tooltip";
import animationProcessing from "url:shared-assets/animations/processing.svg";
import {CONFIG} from "../../constants";
import Stepper from "../../components/Stepper";

interface FeedbackUserInterface {
  email: string;
  name: string;
  favorite: boolean;
  selected: boolean;
}


const SurveyRequestInvite = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [fakeDone, setFakeDone] = useState(false);
  const [done, setDone] = useState(false);
  const [feedbackUsers, setFeedbackUsers] = useState<Array<FeedbackUserInterface>>([]);
  const [deadline, setDeadline] = useState("-");
  const [demo, setDemo] = useState(false);
  const [answered, setAnswered] = useState(0);
  const [error, setError] = useState("");
  const { id } = useParams();

  useEffect(() => {
    getData();
  }, [])

  const getData = () => {
    api.get("client/survey_request_invite/" + id).then(response => {
      if (response.json.status === "ok") {
        setFeedbackUsers(response.json.feedbackUsers);
        setDeadline(response.json.deadline);
        setAnswered(response.json.answered);
        setDemo(response.json.demo);
      } else if (response.json.error) {
        setError(response.json.error);
      } else {
        setError("Unknown error");
      }
      setLoading(false);
    });
  }

  const submit = () => {
    const data = {
      feedbackUsers: feedbackUsers,
      fake_done: fakeDone
    }
    setWaiting(true);
    api.post("client/survey_request_feedback/" + id, data).then(response => {
      if (response.json.status === "ok") {
        setDone(true);
      }
      setWaiting(false);
    })
  }

  if (loading) {
    return (<div className="mt-4">Loading...</div>);
  }

  if (done) {
    return (
      <div className="main-one-col small">
        <div className="mt-4 text-center mx-auto" style={{width: 358}}>
          <IconBadgeCheck className="mt-10 w-12 h-12 mx-auto text-success-500" aria-hidden="true" />
          <div className="mt-6 z-heading1">
            {t("feedback-request-sent")}
          </div>

          <div className="mt-2 text-gray-300">
            {
              lang === "sv" ? "Bra jobbat! Vi hör av oss när ditt feedbackresultat är redo, senast " :
                "Well done! We’ll reach out when the insights report is ready – most likely before "
            }
            {deadline.substring(0, 10)}.
          </div>
          <div className="mt-2 text-gray-300">
            {
              lang === "sv" ? "Under tiden kan du utforska din Profil och få en djupare förståelse för dig själv, eller ta hjälp av AI-assistenten för vardagliga uppgifter." :
                "While you wait, explore your Profile to dive deeper into yourself, or let your AI assistant support you with daily tasks."
            }
          </div>
        </div>

        <div className="mt-6 text-center mx-auto" style={{width: 300}}>
          <div>
            <CustomButton buttonType="outlineWhiteBase" onClick={() => navigate('/ai/0')}>
              {lang === "sv" ? "Använd AI-assistenten" : "Explore AI assistant"}
            </CustomButton>
          </div>

          <div className="mt-2">
            <CustomButton buttonType="blueBase" onClick={() => navigate('/profile')}>
              {lang === "sv" ? "Utforska Profil" : "Explore Profile"}
            </CustomButton>
          </div>

          <div className="mt-2">
            <CustomButton buttonType="transparentBase" onClick={() => navigate('/dev-plan')}>
              {lang === "sv" ? "Tillbaka till målbilder" : "Back to Goals"}
            </CustomButton>
          </div>

        </div>
      </div>
    )
  }

  const formValid = () => {
    let foundSelected = feedbackUsers.filter(f => f.selected).length
    return (foundSelected + answered) >= 3;
  }

  const changeFeedbackUser = (email: string, action: string) => {
    const _feedbackUsers = [...feedbackUsers];
    for (const feedbackUser of _feedbackUsers) {
      if (feedbackUser.email === email) {
        if (action === "selected") {
          feedbackUser.selected = !feedbackUser.selected;
        } else if (action === "favorite") {
          feedbackUser.favorite = !feedbackUser.favorite;
        }
        break;
      }
    }
    setFeedbackUsers(_feedbackUsers);
  };

  return (
    <div className="main-one-col small">
      <div className="mt-4">
        <Stepper
          steps={[
            { title: t("self-assessment"), content: null },
            { title: "Feedback", content: null }
          ]}
          direction="horizontal"
          currentIndex={1}
          completedSteps={[0]}
        />
        <hr className="mt-1 border-indigo-700" />
      </div>
      <div className="flex items-center mt-6">
        <div className="z-h2">{lang === "sv" ? "Skicka feedbackförfrågan" : "Send feedback request"}</div>
        <InformationCircleIcon variant="white" className="w-8 h-8 ml-2 p-1 cursor-pointer hover:bg-indigo-700 rounded-[20px]"
             onClick={() => setShowInfoModal(!showInfoModal)}/>
      </div>

      <div className="mt-6">
        <p className="mb-2 font-medium">
          {t("feedback-search-recipients")}
        </p>
        <AutocompleteSearch
          options={feedbackUsers.filter(
            (fu) => !fu.selected && !fu.favorite
          )}
          getOptionLabel={(contact) => `${contact.name} (${contact.email})`}
          getOptionKey={(contact) => contact.email}
          placeholder={t("feedback-search-by-name")}
          onSelect={(_feedbackUser) =>
            changeFeedbackUser(_feedbackUser.email, "selected")
          }
        />

        {feedbackUsers.filter((fu) => fu.selected || fu.favorite).length >
        0 ? (
          <div className="my-6">
            <div className="z-title2">
              {t("recipients")}
            </div>
            <p className="text-gray-400">
              {t("feedback-recipients-subtitle")}
            </p>
          </div>
        ) : (
          <div className="border border-dashed border-indigo-700 mt-4 mb-4 rounded-md text-center py-10">
            <IconUsers
              className="h-6 w-6 mx-auto mb-4 text-neutral-400"
              aria-hidden="true"
            />

            <div className="z-body1 !text-gray-400">
              <p>
                {lang === "sv"
                  ? "Inga mottagare valda."
                  : "No recipients selected."}
              </p>

              <p className="mt-1 z-body1 !text-gray-400">
                {lang === "sv"
                  ? "Välj minst 3 för att fortsätta."
                  : "Please select at least 3 to continue."}
              </p>
            </div>
          </div>
        )}

        {feedbackUsers
          .filter((fu) => fu.selected || fu.favorite)
          .map((recipient, index) => (
            <div
              className="mt-2 flex items-center justify-between"
              key={"recipient_" + index}
            >
              <div className="flex items-center">
                <div
                  className={
                    "h-6 w-6 rounded flex items-center justify-center cursor-pointer " +
                    (recipient.selected
                      ? "bg-primary-500"
                      : "border border-gray-400")
                  }
                  onClick={() =>
                    changeFeedbackUser(recipient.email, "selected")
                  }
                >
                  {recipient.selected && (
                    <IconCheck className="h-5 w-5 text-neutral-900!" aria-hidden="true" />
                  )}
                </div>
                <p className="ml-2">
                  {recipient.name} ({recipient.email})
                </p>
              </div>
              <Tooltip
                text={t(
                  recipient.favorite
                    ? "remove-as-favourite"
                    : "add-as-favourite"
                )}
                tooltipPosition="left"
              >
                <div className="flex align-middle items-center justify-center  p-1 rounded-full hover:bg-indigo-700">
                  <button
                    onClick={() =>
                      changeFeedbackUser(recipient.email, "favorite")
                    }
                  >
                    {recipient.favorite ? (
                      <StarFilledIcon className="h-4 w-4 text-primary-500" aria-hidden="true" />
                    ) : (
                      <StarOutlineIcon className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </Tooltip>
            </div>
          ))}
      </div>

      {
        (answered > 0) &&
        <div className="mt-6 border border-dashed border-gray-400 p-4 rounded-md">
          {
            lang === "sv" ? "Vi har exkluderat de mottagare som redan svarat på denna förfrågan." :
              "We have excluded the respondents who have answered this feedback request."
          }
        </div>
      }

      {
        waiting ? (
          <div className="mt-6 text-center">
            <img
              src={animationProcessing}
              alt="processing"
              className="mx-auto"
              style={{height: 16, width: 114}}
            />
          </div>
        ) : (
          <CustomButton
            buttonType="blueBase"
            className="mt-6"
            disabled={!formValid()}
            onClick={() => submit()}
          >
            {lang === "sv" ? "Skicka feedbackförfrågan" : "Send feedback request"}
          </CustomButton>
        )
      }

      {
        (!demo && CONFIG.SYSTEM !== "production") &&
          <div className="mt-6">
              <Checkbox id="fake_done" label="Debug: Simulate responses"
                        onChange={() => setFakeDone(!fakeDone)} checked={fakeDone}/>
          </div>
      }

      {
        demo &&
        <div className="mt-6 text-red-500">
          {
            lang === "sv" ? "Detta är ett demokonto - inga feedbackförfrågningar kommer att skickas. " +
              "Du kommer att få låtsasfeedback." :
              "This is a demo account - no feedback requests will be sent. You will get mocked feedback."
          }
        </div>
      }

    </div>
  )
}

export default SurveyRequestInvite;